import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, renderList as _renderList, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["aria-expanded"]
const _hoisted_2 = { class: "fspec__trigger-text" }
const _hoisted_3 = { class: "fspec__head" }
const _hoisted_4 = { class: "fspec__search" }
const _hoisted_5 = { class: "fspec__search-meta" }
const _hoisted_6 = {
  key: 0,
  class: "fspec__section"
}
const _hoisted_7 = { class: "fspec__chips" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 1,
  class: "fspec__section"
}
const _hoisted_10 = { class: "fspec__chips" }
const _hoisted_11 = ["onClick"]
const _hoisted_12 = {
  key: 2,
  class: "fspec__hits",
  role: "listbox"
}
const _hoisted_13 = ["aria-selected", "onMouseenter", "onClick"]
const _hoisted_14 = { class: "fspec__hit-top" }
const _hoisted_15 = ["data-kind"]
const _hoisted_16 = { class: "fspec__hit-page" }
const _hoisted_17 = { class: "fspec__hit-snippet" }
const _hoisted_18 = { class: "fspec__summary" }
const _hoisted_19 = {
  key: 0,
  class: "fspec__section"
}
const _hoisted_20 = { class: "fspec__chips" }
const _hoisted_21 = ["onClick"]
const _hoisted_22 = {
  key: 1,
  class: "fspec__section"
}
const _hoisted_23 = { class: "fspec__chips" }
const _hoisted_24 = ["onClick"]
const _hoisted_25 = { class: "fspec__section" }
const _hoisted_26 = { class: "fspec__section" }
const _hoisted_27 = ["onClick"]
const _hoisted_28 = {
  key: 2,
  class: "fspec__section"
}
const _hoisted_29 = { class: "fspec__notes" }

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  featureSpecOf,
  resolveFeatureSpecNavigation,
  searchFeatureSpecs,
  type FeatureSearchHit,
  type FeatureSpecItem,
} from '@/constants/manageFeatureSpecs'
import { useFeatureTour } from '@/composables/useFeatureTour'

const RECENT_KEY = 'hamuri.featureSpec.recentQueries'
const MAX_RECENT = 6


export default /*@__PURE__*/_defineComponent({
  __name: 'FeatureSpecPanel',
  setup(__props) {

const SELLER_SUGGESTS = ['송장', '발송', '클레임', '재고', '정산', '후기', '계좌']
const ADMIN_SUGGESTS = ['입금확인', '검수', '클레임', '정산', '공지', 'FAQ', '환불', '특가']

const route = useRoute()
const router = useRouter()
const open = ref(false)
const query = ref('')
const activeHit = ref(0)
const rootRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)
const recentQueries = ref<string[]>([])
const { startTour } = useFeatureTour()

const spec = computed(() => featureSpecOf(route.name))

const audience = computed<'seller' | 'admin'>(() => {
  if (spec.value?.audience) return spec.value.audience
  const name = String(route.name || '')
  if (name.startsWith('admin') || String(route.path || '').startsWith('/admin')) return 'admin'
  return 'seller'
})

const audienceLabel = computed(() => (audience.value === 'admin' ? '관리자' : '판매'))
const triggerLabel = computed(() => spec.value?.title || '화면 안내')
const isSearching = computed(() => query.value.length >= 1)
const showSearchView = computed(() => isSearching.value || !spec.value)
const panelTitle = computed(() => {
  if (isSearching.value || !spec.value) return '기능 찾기'
  return spec.value.title
})

const suggestedQueries = computed(() =>
  audience.value === 'admin' ? ADMIN_SUGGESTS : SELLER_SUGGESTS,
)

/** 도움말 버튼 자체 설명은 목록에서 빼서 중복을 줄인다 */
const visibleFeatures = computed(() =>
  (spec.value?.features || []).filter((item) => item.target !== 'manage-feature-search'),
)

const hits = computed(() =>
  searchFeatureSpecs(query.value, { audience: audience.value, limit: 20 }),
)

watch(
  () => route.name,
  () => {
    open.value = false
    query.value = ''
    activeHit.value = 0
  },
)

watch(hits, () => {
  activeHit.value = 0
})

watch(open, async (value) => {
  if (!value) return
  await nextTick()
  /* 모바일·마우스 사용자를 위해 자동 포커스는 데스크톱에서만 */
  if (window.matchMedia('(pointer: fine)').matches) {
    searchInputRef.value?.focus()
  }
})

function loadRecent() {
  try {
    const raw = sessionStorage.getItem(RECENT_KEY)
    const list = raw ? (JSON.parse(raw) as string[]) : []
    recentQueries.value = Array.isArray(list) ? list.filter((item) => typeof item === 'string') : []
  } catch {
    recentQueries.value = []
  }
}

function pushRecent(value: string) {
  const next = value.trim()
  if (next.length < 1) return
  const list = [next, ...recentQueries.value.filter((item) => item !== next)].slice(0, MAX_RECENT)
  recentQueries.value = list
  try {
    sessionStorage.setItem(RECENT_KEY, JSON.stringify(list))
  } catch {
    /* ignore quota */
  }
}

function togglePanel() {
  open.value = !open.value
  if (!open.value) {
    query.value = ''
    activeHit.value = 0
  }
}

function closePanel() {
  open.value = false
  query.value = ''
  activeHit.value = 0
}

function clearQuery() {
  query.value = ''
  activeHit.value = 0
  nextTick(() => searchInputRef.value?.focus())
}

function onStartTour() {
  if (!spec.value) return
  open.value = false
  window.setTimeout(() => {
    if (spec.value) startTour(spec.value)
  }, 80)
}

function onHighlightFeature(item: FeatureSpecItem) {
  if (!spec.value) return
  open.value = false
  window.setTimeout(() => {
    if (spec.value) {
      startTour(spec.value, { featureName: item.name, target: item.target })
    }
  }, 80)
}

async function openHit(hit: FeatureSearchHit) {
  pushRecent(query.value)
  open.value = false
  query.value = ''
  activeHit.value = 0

  const nav = resolveFeatureSpecNavigation(hit.routeName)
  const destName = nav.name
  const remapped = destName !== hit.routeName
  const queryMatches =
    !nav.query ||
    Object.entries(nav.query).every(([key, value]) => String(route.query[key] ?? '') === value)
  const alreadyThere = String(route.name || '') === destName && queryMatches

  try {
    if (!alreadyThere) {
      await router.push(nav.query ? { name: destName, query: nav.query } : { name: destName })
    }
  } catch {
    /* 중복 이동·필수 파라미터·가드 실패 시에도 현재 화면 안내로 이어간다 */
  }

  await nextTick()
  const delay = alreadyThere ? 100 : 420
  window.setTimeout(() => {
    const pageSpec =
      featureSpecOf(remapped ? destName : hit.routeName) || featureSpecOf(hit.routeName)
    if (!pageSpec) {
      open.value = true
      return
    }

    if (hit.kind === 'feature' && (hit.featureName || hit.target)) {
      const targetReady =
        Boolean(hit.target) &&
        Boolean(document.querySelector(`[data-tour="${hit.target}"]`))
      const nameOnPage = Boolean(
        hit.featureName && pageSpec.features.some((item) => item.name === hit.featureName),
      )

      if (!remapped || targetReady || nameOnPage) {
        startTour(pageSpec, { featureName: hit.featureName, target: hit.target })
        return
      }

      /* 편집 화면 등 목록으로 치환된 경우: 대상 UI가 없으면 도움말만 연다 */
      open.value = true
      return
    }

    open.value = true
  }, delay)
}

function onSearchKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    if (!hits.value.length) return
    event.preventDefault()
    activeHit.value = (activeHit.value + 1) % hits.value.length
    return
  }
  if (event.key === 'ArrowUp') {
    if (!hits.value.length) return
    event.preventDefault()
    activeHit.value = (activeHit.value - 1 + hits.value.length) % hits.value.length
    return
  }
  if (event.key === 'Enter') {
    const hit = hits.value[activeHit.value]
    if (hit) {
      event.preventDefault()
      void openHit(hit)
    }
    return
  }
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    if (query.value) {
      clearQuery()
    } else {
      closePanel()
    }
  }
}

function focusableInPanel(): HTMLElement[] {
  const panel = rootRef.value?.querySelector('#manage-feature-spec')
  if (!panel) return []
  return [
    ...panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ),
  ].filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null)
}

function onPanelKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault()
    if (query.value) clearQuery()
    else closePanel()
    return
  }
  if (event.key !== 'Tab' || !open.value) return
  const nodes = focusableInPanel()
  if (nodes.length < 2) return
  const first = nodes[0]
  const last = nodes[nodes.length - 1]
  const active = document.activeElement as HTMLElement | null
  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

function onDocClick(event: MouseEvent) {
  if (!open.value || !rootRef.value) return
  if (!rootRef.value.contains(event.target as Node)) {
    closePanel()
  }
}

function onGlobalKeydown(event: KeyboardEvent) {
  if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'k') return
  const target = event.target as HTMLElement | null
  const tag = target?.tagName?.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
    /* 입력 중에는 브라우저 기본 동작을 방해하지 않는다. 단, 패널이 열려 있으면 포커스만 옮긴다 */
    if (!open.value) return
  }
  event.preventDefault()
  open.value = true
  nextTick(() => searchInputRef.value?.focus())
}

onMounted(() => {
  loadRecent()
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onGlobalKeydown)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "fspec",
    ref_key: "rootRef",
    ref: rootRef
  }, [
    _createElementVNode("button", {
      type: "button",
      class: _normalizeClass(["fspec__trigger", { 'is-open': open.value }]),
      "aria-expanded": open.value,
      "aria-controls": "manage-feature-spec",
      "data-tour": "manage-feature-search",
      title: "이 화면 도움말 열기",
      onClick: togglePanel
    }, [
      _createVNode(AppIcon, {
        icon: "mdi-help-circle-outline",
        size: 18
      }),
      _createElementVNode("span", _hoisted_2, [
        _cache[1] || (_cache[1] = _createElementVNode("strong", null, "도움말", -1)),
        _createElementVNode("em", null, _toDisplayString(triggerLabel.value), 1)
      ])
    ], 10, _hoisted_1),
    _createVNode(_Transition, { name: "fspec-pop" }, {
      default: _withCtx(() => [
        (open.value)
          ? (_openBlock(), _createElementBlock("aside", {
              key: 0,
              id: "manage-feature-spec",
              class: _normalizeClass(["fspec__panel", { 'is-searching': showSearchView.value }]),
              role: "dialog",
              "aria-modal": "true",
              "aria-label": "화면 도움말",
              onKeydown: onPanelKeydown
            }, [
              _createElementVNode("header", _hoisted_3, [
                _createElementVNode("div", null, [
                  _cache[2] || (_cache[2] = _createElementVNode("p", { class: "fspec__eyebrow" }, "화면 도움말", -1)),
                  _createElementVNode("h2", null, _toDisplayString(panelTitle.value), 1)
                ]),
                _createElementVNode("button", {
                  type: "button",
                  class: "fspec__close",
                  "aria-label": "닫기",
                  onClick: closePanel
                }, [
                  _createVNode(AppIcon, {
                    icon: "mdi-close",
                    size: 18
                  })
                ])
              ]),
              _createElementVNode("label", _hoisted_4, [
                _createVNode(AppIcon, {
                  icon: "mdi-magnify",
                  size: 18
                }),
                _withDirectives(_createElementVNode("input", {
                  ref_key: "searchInputRef",
                  ref: searchInputRef,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
                  type: "search",
                  placeholder: "예: 지급일, 클레임, 송장",
                  autocomplete: "off",
                  "aria-label": "기능 검색",
                  onKeydown: onSearchKeydown
                }, null, 544), [
                  [
                    _vModelText,
                    query.value,
                    void 0,
                    { trim: true }
                  ]
                ]),
                (query.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      type: "button",
                      class: "fspec__search-clear",
                      "aria-label": "검색어 지우기",
                      onClick: clearQuery
                    }, [
                      _createVNode(AppIcon, {
                        icon: "mdi-close-circle",
                        size: 16
                      })
                    ]))
                  : _createCommentVNode("", true)
              ]),
              (showSearchView.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createElementVNode("p", _hoisted_5, [
                      (isSearching.value && hits.value.length)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createTextVNode(_toDisplayString(audienceLabel.value) + " 기능 " + _toDisplayString(hits.value.length) + "건 ", 1)
                          ], 64))
                        : (isSearching.value)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                              _createTextVNode("「" + _toDisplayString(query.value) + "」와 맞는 기능이 없습니다.", 1)
                            ], 64))
                          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                              _createTextVNode("아래에서 찾는 말을 누르거나, 위에 검색어를 입력하세요.")
                            ], 64))
                    ]),
                    (!isSearching.value)
                      ? (_openBlock(), _createElementBlock("section", _hoisted_6, [
                          _cache[3] || (_cache[3] = _createElementVNode("h3", null, "자주 찾는 말", -1)),
                          _createElementVNode("div", _hoisted_7, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestedQueries.value, (item) => {
                              return (_openBlock(), _createElementBlock("button", {
                                key: item,
                                type: "button",
                                class: "fspec__chip",
                                onClick: ($event: any) => (query.value = item)
                              }, _toDisplayString(item), 9, _hoisted_8))
                            }), 128))
                          ])
                        ]))
                      : _createCommentVNode("", true),
                    (recentQueries.value.length && !hits.value.length)
                      ? (_openBlock(), _createElementBlock("section", _hoisted_9, [
                          _cache[4] || (_cache[4] = _createElementVNode("h3", null, "최근 검색", -1)),
                          _createElementVNode("div", _hoisted_10, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recentQueries.value, (item) => {
                              return (_openBlock(), _createElementBlock("button", {
                                key: item,
                                type: "button",
                                class: "fspec__chip",
                                onClick: ($event: any) => (query.value = item)
                              }, _toDisplayString(item), 9, _hoisted_11))
                            }), 128))
                          ])
                        ]))
                      : _createCommentVNode("", true),
                    (hits.value.length)
                      ? (_openBlock(), _createElementBlock("ul", _hoisted_12, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(hits.value, (hit, index) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: `${hit.kind}-${hit.routeName}-${hit.title}-${index}`
                            }, [
                              _createElementVNode("button", {
                                type: "button",
                                class: _normalizeClass(["fspec__hit", { 'is-active': index === activeHit.value }]),
                                role: "option",
                                "aria-selected": index === activeHit.value,
                                onMouseenter: ($event: any) => (activeHit.value = index),
                                onClick: ($event: any) => (openHit(hit))
                              }, [
                                _createElementVNode("span", _hoisted_14, [
                                  _createElementVNode("em", {
                                    class: "fspec__hit-kind",
                                    "data-kind": hit.kind
                                  }, _toDisplayString(hit.matchedIn), 9, _hoisted_15),
                                  _createElementVNode("span", _hoisted_16, _toDisplayString(hit.pageTitle), 1)
                                ]),
                                _createElementVNode("strong", null, _toDisplayString(hit.title), 1),
                                _createElementVNode("span", _hoisted_17, _toDisplayString(hit.snippet), 1)
                              ], 42, _hoisted_13)
                            ]))
                          }), 128))
                        ]))
                      : _createCommentVNode("", true),
                    _cache[5] || (_cache[5] = _createElementVNode("p", { class: "fspec__hint" }, "결과를 누르면 해당 화면으로 이동하고, 위치를 표시합니다.", -1))
                  ], 64))
                : (spec.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("p", _hoisted_18, _toDisplayString(spec.value.summary), 1),
                      _createElementVNode("button", {
                        type: "button",
                        class: "fspec__tour-btn",
                        onClick: onStartTour
                      }, [
                        _createVNode(AppIcon, {
                          icon: "mdi-play-circle-outline",
                          size: 18
                        }),
                        _cache[6] || (_cache[6] = _createTextVNode(" 화면에서 순서대로 보기 ", -1))
                      ]),
                      (suggestedQueries.value.length)
                        ? (_openBlock(), _createElementBlock("section", _hoisted_19, [
                            _cache[7] || (_cache[7] = _createElementVNode("h3", null, "다른 화면 찾기", -1)),
                            _createElementVNode("div", _hoisted_20, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestedQueries.value.slice(0, 6), (item) => {
                                return (_openBlock(), _createElementBlock("button", {
                                  key: item,
                                  type: "button",
                                  class: "fspec__chip",
                                  onClick: ($event: any) => (query.value = item)
                                }, _toDisplayString(item), 9, _hoisted_21))
                              }), 128))
                            ])
                          ]))
                        : _createCommentVNode("", true),
                      (recentQueries.value.length)
                        ? (_openBlock(), _createElementBlock("section", _hoisted_22, [
                            _cache[8] || (_cache[8] = _createElementVNode("h3", null, "최근 검색", -1)),
                            _createElementVNode("div", _hoisted_23, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recentQueries.value, (item) => {
                                return (_openBlock(), _createElementBlock("button", {
                                  key: item,
                                  type: "button",
                                  class: "fspec__chip",
                                  onClick: ($event: any) => (query.value = item)
                                }, _toDisplayString(item), 9, _hoisted_24))
                              }), 128))
                            ])
                          ]))
                        : _createCommentVNode("", true),
                      _createElementVNode("section", _hoisted_25, [
                        _cache[9] || (_cache[9] = _createElementVNode("h3", null, "이 화면에서 하는 일", -1)),
                        _createElementVNode("p", null, _toDisplayString(spec.value.purpose), 1)
                      ]),
                      _createElementVNode("section", _hoisted_26, [
                        _cache[10] || (_cache[10] = _createElementVNode("h3", null, "기능 목록", -1)),
                        _cache[11] || (_cache[11] = _createElementVNode("p", { class: "fspec__section-hint" }, "항목을 누르면 화면에서 위치를 보여 줍니다.", -1)),
                        _createElementVNode("ul", null, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleFeatures.value, (item) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: item.name
                            }, [
                              (item.target)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 0,
                                    type: "button",
                                    class: "fspec__feature-btn",
                                    onClick: ($event: any) => (onHighlightFeature(item))
                                  }, [
                                    _createElementVNode("strong", null, _toDisplayString(item.name), 1),
                                    _createElementVNode("span", null, _toDisplayString(item.description), 1)
                                  ], 8, _hoisted_27))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createElementVNode("strong", null, _toDisplayString(item.name), 1),
                                    _createElementVNode("span", null, _toDisplayString(item.description), 1)
                                  ], 64))
                            ]))
                          }), 128))
                        ])
                      ]),
                      (spec.value.notes?.length)
                        ? (_openBlock(), _createElementBlock("section", _hoisted_28, [
                            _cache[12] || (_cache[12] = _createElementVNode("h3", null, "참고", -1)),
                            _createElementVNode("ul", _hoisted_29, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(spec.value.notes, (note) => {
                                return (_openBlock(), _createElementBlock("li", { key: note }, _toDisplayString(note), 1))
                              }), 128))
                            ])
                          ]))
                        : _createCommentVNode("", true)
                    ], 64))
                  : _createCommentVNode("", true)
            ], 34))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 512))
}
}

})